/**
 * Vertical Alignment Service
 * 
 * Eliminates keystoning in real estate photos by detecting and correcting
 * vertical architectural lines to be perfectly parallel to image borders.
 */

declare var cv: any; // OpenCV.js global

export interface AlignmentResult {
  correctedImageData: string;
  angle: number;
  success: boolean;
  error?: string;
}

/**
 * Load OpenCV.js if not already loaded
 */
export const ensureOpenCVLoaded = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof cv !== 'undefined' && cv.Mat) {
      resolve();
      return;
    }

    // Check if already loading
    if ((window as any).opencvLoadingPromise) {
      (window as any).opencvLoadingPromise.then(resolve).catch(reject);
      return;
    }

    // Create loading promise
    (window as any).opencvLoadingPromise = new Promise((res, rej) => {
      const script = document.createElement('script');
      script.src = 'https://docs.opencv.org/4.9.0/opencv.js';
      script.async = true;
      
      script.onload = () => {
        // Wait for cv to be fully initialized
        const checkCV = setInterval(() => {
          if (typeof cv !== 'undefined' && cv.Mat) {
            clearInterval(checkCV);
            res();
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkCV);
          if (typeof cv === 'undefined' || !cv.Mat) {
            rej(new Error('OpenCV failed to initialize'));
          }
        }, 10000);
      };
      
      script.onerror = () => rej(new Error('Failed to load OpenCV.js'));
      document.head.appendChild(script);
    });

    (window as any).opencvLoadingPromise.then(resolve).catch(reject);
  });
};

/**
 * Detect architectural features (windows, doors, wall corners)
 */
interface DetectedFeature {
  type: 'window' | 'door' | 'corner' | 'wall';
  confidence: number;
  angle: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

const detectArchitecturalFeatures = (src: any): DetectedFeature[] => {
  const gray = new cv.Mat();
  const edges = new cv.Mat();
  const features: DetectedFeature[] = [];
  
  try {
    // Convert to grayscale
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    
    // Apply Gaussian blur to reduce noise
    cv.GaussianBlur(gray, gray, new cv.Size(5, 5), 1.5);
    
    // Detect edges using Canny with adaptive thresholds
    cv.Canny(gray, edges, 50, 150);
    
    // Find contours to detect rectangular features (windows, doors)
    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();
    cv.findContours(edges, contours, hierarchy, cv.RETR_TREE, cv.CHAIN_APPROX_SIMPLE);
    
    // Analyze contours to find rectangular features
    for (let i = 0; i < contours.size(); i++) {
      const contour = contours.get(i);
      const area = cv.contourArea(contour);
      
      // Filter by size - looking for significant features
      const minArea = (src.rows * src.cols) * 0.02; // At least 2% of image
      const maxArea = (src.rows * src.cols) * 0.4;  // At most 40% of image
      
      if (area < minArea || area > maxArea) {
        contour.delete();
        continue;
      }
      
      // Approximate polygon
      const peri = cv.arcLength(contour, true);
      const approx = new cv.Mat();
      cv.approxPolyDP(contour, approx, 0.02 * peri, true);
      
      // Look for rectangles (4 corners)
      if (approx.rows === 4) {
        const rect = cv.boundingRect(contour);
        const aspectRatio = rect.width / rect.height;
        
        // Classify feature based on aspect ratio and position
        let featureType: 'window' | 'door' | 'wall' = 'wall';
        let confidence = 0.5;
        
        // Windows: typically square-ish or horizontal rectangles in upper half
        if (aspectRatio > 0.6 && aspectRatio < 2.5 && rect.y < src.rows * 0.7) {
          featureType = 'window';
          confidence = 0.8;
        }
        // Doors: tall vertical rectangles
        else if (aspectRatio < 0.6 && rect.height > src.rows * 0.3) {
          featureType = 'door';
          confidence = 0.9;
        }
        
        // Calculate angle of the rectangle edges
        const minRect = cv.minAreaRect(contour);
        let angle = minRect.angle;
        
        // Normalize angle to be deviation from vertical
        if (angle < -45) angle += 90;
        if (angle > 45) angle -= 90;
        
        features.push({
          type: featureType,
          confidence: confidence,
          angle: angle,
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height
        });
      }
      
      approx.delete();
      contour.delete();
    }
    
    contours.delete();
    hierarchy.delete();
    
    // Also detect strong vertical lines as fallback
    const lines = new cv.Mat();
    cv.HoughLinesP(edges, lines, 1, Math.PI / 180, 80, src.rows * 0.2, 10);
    
    for (let i = 0; i < lines.rows; i++) {
      const x1 = lines.data32S[i * 4];
      const y1 = lines.data32S[i * 4 + 1];
      const x2 = lines.data32S[i * 4 + 2];
      const y2 = lines.data32S[i * 4 + 3];
      
      const length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
      const dx = x2 - x1;
      const dy = y2 - y1;
      
      // Only consider long vertical lines
      if (length > src.rows * 0.3) {
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        const verticalAngle = angle - 90; // Convert to deviation from vertical
        
        // Check if near-vertical (within 20 degrees)
        if (Math.abs(verticalAngle) < 20) {
          features.push({
            type: 'corner',
            confidence: 0.6,
            angle: verticalAngle,
            x: Math.min(x1, x2),
            y: Math.min(y1, y2),
            width: Math.abs(x2 - x1),
            height: Math.abs(y2 - y1)
          });
        }
      }
    }
    
    lines.delete();
    return features;
    
  } finally {
    gray.delete();
    edges.delete();
  }
};

/**
 * Detect dominant vertical lines in the image (legacy method for fallback)
 */
const detectVerticalLines = (src: any): number[] => {
  const gray = new cv.Mat();
  const edges = new cv.Mat();
  const lines = new cv.Mat();
  
  try {
    // Convert to grayscale
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    
    // Apply Gaussian blur to reduce noise
    cv.GaussianBlur(gray, gray, new cv.Size(5, 5), 1.5);
    
    // Detect edges using Canny
    cv.Canny(gray, edges, 50, 150);
    
    // Detect lines using Hough Line Transform (threshold 60 = more sensitive, catches more verticals)
    cv.HoughLines(edges, lines, 1, Math.PI / 180, 60);
    
    const angles: number[] = [];
    
    // Extract angles of near-vertical lines (within 20 degrees of vertical)
    for (let i = 0; i < lines.rows; i++) {
      const rho = lines.data32F[i * 2];
      const theta = lines.data32F[i * 2 + 1];
      
      // Convert to degrees
      const angleDeg = (theta * 180) / Math.PI;
      
      // Check if line is near-vertical (0° or 180° ± 20°)
      // In Hough space, vertical lines have theta near 0 or 180
      const distFrom0 = Math.abs(angleDeg);
      const distFrom180 = Math.abs(angleDeg - 180);
      const distFromVertical = Math.min(distFrom0, distFrom180);
      
      if (distFromVertical < 20) {
        // Calculate deviation from perfect vertical
        const deviation = distFrom0 < distFrom180 ? angleDeg : angleDeg - 180;
        angles.push(deviation);
      }
    }
    
    return angles;
  } finally {
    gray.delete();
    edges.delete();
    lines.delete();
  }
};

/**
 * Calculate the median angle from detected lines
 */
const calculateMedianAngle = (angles: number[]): number => {
  if (angles.length === 0) return 0;
  
  const sorted = [...angles].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
};

/**
 * Apply perspective correction to straighten vertical lines
 */
export const applyPerspectiveCorrection = (src: any, angle: number): any => {
  const dst = new cv.Mat();
  
  try {
    // Get image center
    const center = new cv.Point(src.cols / 2, src.rows / 2);
    
    // Calculate rotation matrix
    const rotationMatrix = cv.getRotationMatrix2D(center, angle, 1.0);
    
    // Calculate new bounding dimensions
    const cos = Math.abs(rotationMatrix.data64F[0]);
    const sin = Math.abs(rotationMatrix.data64F[1]);
    
    const newWidth = Math.round(src.rows * sin + src.cols * cos);
    const newHeight = Math.round(src.rows * cos + src.cols * sin);
    
    // Adjust rotation matrix for new dimensions
    rotationMatrix.data64F[2] += (newWidth / 2) - center.x;
    rotationMatrix.data64F[5] += (newHeight / 2) - center.y;
    
    // Apply affine transformation
    cv.warpAffine(
      src,
      dst,
      rotationMatrix,
      new cv.Size(newWidth, newHeight),
      cv.INTER_LINEAR,
      cv.BORDER_CONSTANT,
      new cv.Scalar(0, 0, 0, 0)
    );
    
    rotationMatrix.delete();
    return dst;
  } catch (error) {
    dst.delete();
    throw error;
  }
};

/**
 * Smart crop to remove transparent edges while maintaining aspect ratio
 */
export const smartCrop = (src: any, originalAspectRatio: number): any => {
  const gray = new cv.Mat();
  const mask = new cv.Mat();
  
  try {
    // Convert to grayscale
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    
    // Create binary mask of non-transparent pixels
    cv.threshold(gray, mask, 1, 255, cv.THRESH_BINARY);
    
    // Find bounding rectangle of non-zero pixels
    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();
    cv.findContours(mask, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
    
    let maxRect = new cv.Rect(0, 0, src.cols, src.rows);
    
    if (contours.size() > 0) {
      // Get bounding box of largest contour
      const contour = contours.get(0);
      maxRect = cv.boundingRect(contour);
      contour.delete();
    }
    
    contours.delete();
    hierarchy.delete();
    
    // Calculate crop dimensions maintaining aspect ratio
    let cropWidth = maxRect.width;
    let cropHeight = maxRect.height;
    const currentRatio = cropWidth / cropHeight;
    
    if (currentRatio > originalAspectRatio) {
      // Too wide, constrain by height
      cropWidth = Math.floor(cropHeight * originalAspectRatio);
    } else if (currentRatio < originalAspectRatio) {
      // Too tall, constrain by width
      cropHeight = Math.floor(cropWidth / originalAspectRatio);
    }
    
    // Center the crop
    const cropX = maxRect.x + Math.floor((maxRect.width - cropWidth) / 2);
    const cropY = maxRect.y + Math.floor((maxRect.height - cropHeight) / 2);
    
    // Ensure crop is within bounds
    const finalCropX = Math.max(0, Math.min(cropX, src.cols - cropWidth));
    const finalCropY = Math.max(0, Math.min(cropY, src.rows - cropHeight));
    const finalCropWidth = Math.min(cropWidth, src.cols - finalCropX);
    const finalCropHeight = Math.min(cropHeight, src.rows - finalCropY);
    
    // Extract ROI
    const rect = new cv.Rect(finalCropX, finalCropY, finalCropWidth, finalCropHeight);
    const cropped = src.roi(rect);
    
    return cropped;
  } finally {
    gray.delete();
    mask.delete();
  }
};

/**
 * Convert OpenCV Mat to base64 data URL
 */
export const matToDataURL = (mat: any): string => {
  const canvas = document.createElement('canvas');
  cv.imshow(canvas, mat);
  return canvas.toDataURL('image/png');
};

/**
 * Select best angle from detected features (prioritize windows and doors)
 */
const selectBestAlignmentAngle = (features: DetectedFeature[]): { angle: number; feature: string } | null => {
  if (features.length === 0) return null;
  
  // Sort by priority: windows > doors > corners > walls
  const priorityOrder = { window: 4, door: 3, corner: 2, wall: 1 };
  
  const sortedFeatures = features.sort((a, b) => {
    // First by type priority
    const priorityDiff = priorityOrder[b.type] - priorityOrder[a.type];
    if (priorityDiff !== 0) return priorityDiff;
    // Then by confidence
    return b.confidence - a.confidence;
  });
  
  // Group features by type to get consensus
  const windowFeatures = sortedFeatures.filter(f => f.type === 'window');
  const doorFeatures = sortedFeatures.filter(f => f.type === 'door');
  const cornerFeatures = sortedFeatures.filter(f => f.type === 'corner');
  
  // Prefer windows if we have at least one
  if (windowFeatures.length > 0) {
    const angles = windowFeatures.map(f => f.angle);
    return {
      angle: calculateMedianAngle(angles),
      feature: `window (${windowFeatures.length} detected)`
    };
  }
  
  // Then doors
  if (doorFeatures.length > 0) {
    const angles = doorFeatures.map(f => f.angle);
    return {
      angle: calculateMedianAngle(angles),
      feature: `door frame (${doorFeatures.length} detected)`
    };
  }
  
  // Then wall corners
  if (cornerFeatures.length > 0) {
    const angles = cornerFeatures.map(f => f.angle);
    return {
      angle: calculateMedianAngle(angles),
      feature: `wall edge (${cornerFeatures.length} detected)`
    };
  }
  
  // Fallback to any features
  const angles = sortedFeatures.map(f => f.angle);
  return {
    angle: calculateMedianAngle(angles),
    feature: 'architectural feature'
  };
};

/**
 * Main function: Auto-straighten vertical lines in image
 */
export const autoStraightenImage = async (
  imageDataUrl: string
): Promise<AlignmentResult> => {
  try {
    // Ensure OpenCV is loaded
    await ensureOpenCVLoaded();
    
    // Load image into canvas
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = imageDataUrl;
    });
    
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');
    
    ctx.drawImage(img, 0, 0);
    
    // Convert to OpenCV Mat
    const src = cv.imread(canvas);
    const originalAspectRatio = src.cols / src.rows;
    
    try {
      // Detect architectural features (windows, doors, corners)
      console.log('Detecting architectural features...');
      const features = detectArchitecturalFeatures(src);
      
      console.log(`Found ${features.length} architectural features:`, 
        features.reduce((acc, f) => {
          acc[f.type] = (acc[f.type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      );
      
      // Select best alignment angle based on detected features
      const result = selectBestAlignmentAngle(features);
      
      if (!result) {
        // Fallback to simple line detection (relaxed: need at least 1 strong vertical line)
        console.log('No architectural features found, using line detection...');
        const angles = detectVerticalLines(src);
        
        if (angles.length < 2) {
          src.delete();
          return {
            correctedImageData: imageDataUrl,
            angle: 0,
            success: false,
            error: 'Not enough vertical lines detected. Try "Draw Vertical Line" and click two points on a wall edge.'
          };
        }
        
        const medianAngle = calculateMedianAngle(angles);
        
        // Skip if angle is very small (already aligned)
        if (Math.abs(medianAngle) < 0.5) {
          src.delete();
          return {
            correctedImageData: imageDataUrl,
            angle: 0,
            success: true,
            error: 'Image is already well-aligned.'
          };
        }
        
        console.log(`Correcting by ${medianAngle.toFixed(2)}° based on detected lines`);
        
        // Apply perspective correction
        const corrected = applyPerspectiveCorrection(src, medianAngle);
        
        // Smart crop to remove transparent areas
        const cropped = smartCrop(corrected, originalAspectRatio);
        
        // Convert back to data URL
        const resultDataUrl = matToDataURL(cropped);
        
        // Cleanup
        src.delete();
        corrected.delete();
        cropped.delete();
        
        return {
          correctedImageData: resultDataUrl,
          angle: medianAngle,
          success: true
        };
      }
      
      // Skip if angle is very small (already aligned)
      if (Math.abs(result.angle) < 0.5) {
        src.delete();
        return {
          correctedImageData: imageDataUrl,
          angle: 0,
          success: true,
          error: 'Image is already well-aligned.'
        };
      }
      
      console.log(`Aligning to ${result.feature}, correcting by ${result.angle.toFixed(2)}°`);
      
      // Apply perspective correction
      const corrected = applyPerspectiveCorrection(src, result.angle);
      
      // Smart crop to remove transparent areas
      const cropped = smartCrop(corrected, originalAspectRatio);
      
      // Convert back to data URL
      const resultDataUrl = matToDataURL(cropped);
      
      // Cleanup
      src.delete();
      corrected.delete();
      cropped.delete();
      
      return {
        correctedImageData: resultDataUrl,
        angle: result.angle,
        success: true
      };
    } catch (error) {
      src.delete();
      throw error;
    }
  } catch (error: any) {
    console.error('Auto-straighten error:', error);
    return {
      correctedImageData: imageDataUrl,
      angle: 0,
      success: false,
      error: error.message || 'Failed to process image'
    };
  }
};

/**
 * Draw 5x5 reference grid overlay on canvas
 */
export const drawAlignmentGrid = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  color: string = 'rgba(255, 255, 255, 0.3)',
  lineWidth: number = 1
): void => {
  const width = canvas.width;
  const height = canvas.height;
  
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  
  // Draw 5 vertical lines (including borders)
  for (let i = 0; i <= 5; i++) {
    const x = (width / 5) * i;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  
  // Draw 5 horizontal lines (including borders)
  for (let i = 0; i <= 5; i++) {
    const y = (height / 5) * i;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }
  
  // Emphasize center lines
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.lineWidth = lineWidth * 1.5;
  
  // Center vertical line
  ctx.beginPath();
  ctx.moveTo(width / 2, 0);
  ctx.lineTo(width / 2, height);
  ctx.stroke();
  
  // Center horizontal line
  ctx.beginPath();
  ctx.moveTo(0, height / 2);
  ctx.lineTo(width, height / 2);
  ctx.stroke();
  
  ctx.restore();
};
